import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatPrice } from "@/utils/pricing-formatter";

interface QuotePriceFieldProps {
  /** The product's own per-mile rate at the quoted quantity, if known. */
  calculatedPricePerMile: number | undefined;
  /** What staff typed, or null while the field follows the calculated rate. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Miles the base total is shown for, already capped to the maximum. */
  miles: number | undefined;
}

/**
 * Per-mile rate for a quote: shows the calculated rate until staff type their
 * own, which then holds at any quantity the customer picks.
 */
export function QuotePriceField({
  calculatedPricePerMile,
  value,
  onChange,
  miles,
}: Readonly<QuotePriceFieldProps>) {
  const isOverridden = value !== null;
  const shownValue = value ?? calculatedPricePerMile?.toString() ?? "";
  const pricePerMile = Number(shownValue);
  const baseTotal =
    miles && shownValue !== "" && Number.isFinite(pricePerMile)
      ? miles * pricePerMile
      : undefined;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <Label>Price per mile</Label>
        {isOverridden && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => onChange(null)}
          >
            Use calculated
          </Button>
        )}
      </div>
      <Input
        type="number"
        min={0}
        step="any"
        value={shownValue}
        onChange={(event) => onChange(event.target.value)}
        placeholder="0.016"
      />
      <p className="text-xs text-muted-foreground">
        {isOverridden
          ? `Overridden${
              calculatedPricePerMile === undefined
                ? ""
                : ` (calculated: ${calculatedPricePerMile})`
            }. The customer gets this rate at any quantity.`
          : "Calculated from the product's price tiers."}{" "}
        {baseTotal !== undefined &&
          `Base total ${formatPrice({ price: baseTotal })}, before status bonus and promo code.`}
      </p>
    </div>
  );
}
